import React from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'wsm-common-data';
import {
	buildPaginationUrl,
	getCurrentPageIndex,
	getPaginationItems,
	getTotalPages,
	stripPersonalizationParams
} from 'wsm-srp-utilities';

/**
 * Static, crawlable pagination nav rendered on the AI-bot SSR path
 * (US1994526 / TA4069141, AC6). Plain <a href="…?start=<offset>"> links so a
 * rendering crawler can follow to subsequent SRP/TRP pages with no JS.
 *
 * It MIRRORS the interactive ws-inv-paging widget: same windowed page-set with
 * ellipsis gaps (via the shared getPaginationItems — NOT one link per page) and
 * the same nav > ul.pagination markup/classes, so it looks like the real widget
 * while staying static. It lives inside Widget.jsx's bot-only isSeoPhase subtree, so
 * it is replaced on hydration / Phase B and never lingers beside the real
 * ws-inv-paging widget for a misidentified human (ADR-0014 Decision step 6).
 *
 * Renders purely from props seeded into the `seoVehicleData` slice server-side
 * (`pageInfo`, `baseUrl`, `parameterMap`) — with no live window/location reads
 * and no `useRequestData()`. ws-scripts serializes only a subset of requestData
 * to the client (uri/parameterMap are dropped), so reading them from the hook
 * would render path-less hrefs on the client and mismatch the SSR markup;
 * sourcing them from the fully-serialized slice keeps server + client identical
 * (clean hydrate, AC5). useLabels() resolves synchronously during renderToString.
 *
 * Hrefs come from wsm-srp-utilities' shared `buildPaginationUrl` (the canonical
 * SRP `start=<offset>` builder). Geo / personalization params are stripped first
 * (stripPersonalizationParams, per ADR-0014 D1) so the crawlable URLs carry only
 * content-defining params — facets + sort + the start offset are kept; geo is
 * dropped (the bot content is geo-neutral). ws-inv-data's head rel=prev/next may
 * still carry geo (the complementary hint); the body anchors are the clean signal.
 */
const SeoPagination = ({
	pageInfo,
	baseUrl,
	parameterMap: rawParameterMap
}) => {
	const labels = useLabels();

	// Crawlable links carry only content-defining params — strip geo /
	// personalization (ADR-0014 D1). `parameterMap` is the cleaned set below.
	const parameterMap = stripPersonalizationParams(rawParameterMap);

	const { pageStart = 0, pageSize = 0, totalCount = 0 } = pageInfo || {};

	// Nothing to paginate: no page size, or a single page of results.
	const totalPages = getTotalPages(totalCount, pageSize);
	if (totalPages <= 1) {
		return null;
	}

	const currentPageIndex = getCurrentPageIndex(pageStart, pageSize);
	const lastPageIndex = totalPages - 1;

	// Base path the links resolve against — query stripped. Seeded into the
	// seoVehicleData slice from requestData.uri server-side (see the slice doc).
	const pageUrl = (baseUrl || '').split('?')[0];

	const items = getPaginationItems(currentPageIndex, {
		totalCount,
		pageSize
	});

	const goToPageTemplate = labels.get('GO_TO_PAGE_X');

	const pageItems = items.map((item) => {
		if (item.type === 'ellipsis') {
			return (
				<li
					key={item.key}
					className="pagination-ellipsis"
					aria-hidden="true"
				>
					<span>&hellip;</span>
				</li>
			);
		}

		const { pageIndex, pageNumber } = item;
		const isCurrent = pageIndex === currentPageIndex;

		return (
			<li key={item.key} className={isCurrent ? 'active' : null}>
				<a
					href={buildPaginationUrl(
						pageIndex,
						pageSize,
						parameterMap,
						pageUrl
					)}
					aria-current={isCurrent ? 'page' : null}
					aria-label={
						isCurrent
							? null
							: goToPageTemplate.replace(
									'{pageNumber}',
									pageNumber
							  )
					}
				>
					{pageNumber}
				</a>
			</li>
		);
	});

	return (
		<div className="d-flex justify-content-end">
			<nav aria-label={labels.get('MATCHING_RESULTS_PAGES')}>
				<ul className="pagination">
					{currentPageIndex > 0 && (
						<li className="pagination-previous">
							<a
								href={buildPaginationUrl(
									currentPageIndex - 1,
									pageSize,
									parameterMap,
									pageUrl
								)}
								aria-label={labels.get('GO_TO_PREVIOUS_PAGE')}
							>
								<i
									className="ddc-icon ddc-icon-arrow1-left"
									aria-hidden="true"
								/>
							</a>
						</li>
					)}
					{pageItems}
					{currentPageIndex < lastPageIndex && (
						<li className="pagination-next">
							<a
								href={buildPaginationUrl(
									currentPageIndex + 1,
									pageSize,
									parameterMap,
									pageUrl
								)}
								aria-label={labels.get('GO_TO_NEXT_PAGE')}
							>
								<i
									className="ddc-icon ddc-icon-arrow1-right"
									aria-hidden="true"
								/>
							</a>
						</li>
					)}
				</ul>
			</nav>
		</div>
	);
};

SeoPagination.propTypes = {
	pageInfo: PropTypes.shape({
		pageStart: PropTypes.number,
		pageSize: PropTypes.number,
		totalCount: PropTypes.number
	}),
	// Seeded into the seoVehicleData slice from requestData server-side.
	baseUrl: PropTypes.string,
	parameterMap: PropTypes.objectOf(
		PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.arrayOf(PropTypes.string)
		])
	)
};

SeoPagination.defaultProps = {
	pageInfo: {},
	baseUrl: '',
	parameterMap: {}
};

export default SeoPagination;
